"use client"

import {UiErrorText} from "@/components/i18n/ui-error-text"
import {useUiControlLabels} from "@/components/i18n/ui-control-labels-context"
import { useState } from "react"
import { BookOpen, Bot, CheckCircle2, Loader2, RotateCcw } from "lucide-react"

import { RestartOnboardingConfirmationDialog } from "@/components/onboarding/restart-onboarding-confirmation-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import { useRestartAdminSignupOnboardingMutation } from "@/hooks/mutations/admin-signup-onboarding"
import { useAdminSignupOnboardingDetailsQuery } from "@/hooks/queries/admin-signup-onboarding"
import {
  isSignupOnboardingSocialKnowledgeSource,
  SIGNUP_ONBOARDING_STEP_LABELS,
  type SignupOnboardingState,
} from "@/lib/onboarding/signup-onboarding"
import type { SignupOnboardingKnowledgeDocument } from "@/lib/onboarding/signup-onboarding-store"
import { useTranslations } from "next-intl"



function formatValue(value: string): string {
  return value.replaceAll("_", " ").replace(/^\w/, (letter) =>
    letter.toUpperCase(),
  )
}

function formatDate(value: string | null, dateLocale: string = "en-US"): string {
  if (!value) return "—"
  return new Date(value).toLocaleString(dateLocale)
}

export function TenantOnboardingDialog({
  tenant,
  onOpenChange,
}: {
  tenant: { id: string; name: string } | null
  onOpenChange: (open: boolean) => void
}) {
  const {locale: uiDateLocale} = useUiControlLabels()


  const tUi = useTranslations("TenantManagement.TenantManagementTenantOnboardingDialog")
  const tCommonUi = useTranslations("Common")

  const [restartConfirmationOpen, setRestartConfirmationOpen] = useState(false)
  const details = useAdminSignupOnboardingDetailsQuery(tenant?.id ?? null)
  const restart = useRestartAdminSignupOnboardingMutation()
  const state = details.data?.state
  const updatedAt = details.data?.updated_at ?? null
  const knowledgeDocuments = details.data?.knowledge_documents ?? []

  const handleRestart = async () => {
    if (!tenant) return
    await restart.mutateAsync(tenant.id)
    setRestartConfirmationOpen(false)
  }

  return (
    <>
      <Dialog open={Boolean(tenant)} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle><span key={tUi("signupOnboarding")}>{tUi("signupOnboarding")}</span></DialogTitle>
          <DialogDescription>
            {<span key={tUi("progressAndInformationEnteredByValue0", { value0: String(tenant?.name ?? '') })}>{tUi("progressAndInformationEnteredByValue0", { value0: String(tenant?.name ?? '') })}</span>}
          </DialogDescription>
        </DialogHeader>

        {details.isLoading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            <span><span key={tUi("loadingOnboardingDetails")}>{tUi("loadingOnboardingDetails")}</span></span>
          </div>
        ) : details.error ? (
          <p className="py-8 text-sm text-destructive">
            <UiErrorText message={details.error.message} />
          </p>
        ) : state ? (
          <ScrollArea className="max-h-[70vh] pr-4">
            <div className="space-y-5 pb-2">
              <div className="grid gap-3 rounded-lg border p-4 sm:grid-cols-3">
                <div>
                  <p className="text-xs text-muted-foreground"><span key={tUi("status")}>{tUi("status")}</span></p>
                  <Badge className="mt-1" variant="outline">
                    {formatValue(state.status)}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground"><span key={tUi("currentStep")}>{tUi("currentStep")}</span></p>
                  <p className="mt-1 text-sm font-medium">
                    {SIGNUP_ONBOARDING_STEP_LABELS[state.current_step]}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground"><span key={tUi("activeAgent")}>{tUi("activeAgent")}</span></p>
                  <p key={state.agents.length
                      ? tUi("value0OfValue1", { value0: String(state.active_agent_index + 1), value1: String(state.agents.length) })
                      : tUi("noneSelected")} className="mt-1 text-sm font-medium">
                    {state.agents.length
                      ? tUi("value0OfValue1", { value0: String(state.active_agent_index + 1), value1: String(state.agents.length) })
                      : tUi("noneSelected")}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground"><span key={tUi("lastUpdate")}>{tUi("lastUpdate")}</span></p>
                  <p className="mt-1 text-sm">{formatDate(updatedAt, uiDateLocale)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground"><span key={tCommonUi("completed")}>{tCommonUi("completed")}</span></p>
                  <p className="mt-1 text-sm">{formatDate(state.completed_at, uiDateLocale)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground"><span key={tUi("skipped")}>{tUi("skipped")}</span></p>
                  <p className="mt-1 text-sm">{formatDate(state.skipped_at, uiDateLocale)}</p>
                </div>
              </div>

              <section className="rounded-lg border p-4">
                <div className="flex items-center gap-2 font-medium">
                  <BookOpen className="h-4 w-4 text-blue-600" />
                  <span><span key={tUi("knowledgeBaseImport")}>{tUi("knowledgeBaseImport")}</span></span>
                </div>
                <div className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
                  <div>
                    <p className="text-xs text-muted-foreground"><span key={tUi("source")}>{tUi("source")}</span></p>
                    <p className="mt-1">
                      {getKnowledgeSourceLabel(state, tUi)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground"><span key={tUi("status")}>{tUi("status")}</span></p>
                    <p className="mt-1">
                      {getKnowledgeImportStatus(state, knowledgeDocuments)}
                    </p>
                  </div>
                  {state.knowledge_source === "website" ? (
                    <>
                      <div>
                        <p className="text-xs text-muted-foreground"><span key={tUi("importJob")}>{tUi("importJob")}</span></p>
                        <p className="mt-1 break-all font-mono text-xs">
                          {state.website.job_id}
                        </p>
                      </div>
                      <div className="sm:col-span-2">
                        <p className="text-xs text-muted-foreground"><span key={tUi("websiteURL")}>{tUi("websiteURL")}</span></p>
                        <p className="mt-1 break-all">{state.website.url ?? "—"}</p>
                      </div>
                    </>
                  ) : null}
                  {isSignupOnboardingSocialKnowledgeSource(
                    state.knowledge_source,
                  ) ? (
                    <>
                      <div>
                        <p className="text-xs text-muted-foreground"><span key={tUi("importJob")}>{tUi("importJob")}</span></p>
                        <p className="mt-1 break-all font-mono text-xs">
                          {state.social.job_id}
                        </p>
                      </div>
                      <div className="sm:col-span-2">
                        <p className="text-xs text-muted-foreground"><span key={tUi("socialProfileURL")}>{tUi("socialProfileURL")}</span></p>
                        <p className="mt-1 break-all">{state.social.url ?? "—"}</p>
                      </div>
                    </>
                  ) : null}
                  {state.knowledge_source === "shopify" ? (
                    <div className="sm:col-span-2">
                      <p className="text-xs text-muted-foreground"><span key={tUi("shopifyStore")}>{tUi("shopifyStore")}</span></p>
                      <p className="mt-1 break-all">
                        {state.shopify_shop_domain ?? "—"}
                      </p>
                    </div>
                  ) : null}
                  {state.knowledge_source === "documents" ? (
                    <div className="sm:col-span-2">
                      <p className="text-xs text-muted-foreground">
                        {<span key={tUi("documentsValue0", { value0: String(knowledgeDocuments.length) })}>{tUi("documentsValue0", { value0: String(knowledgeDocuments.length) })}</span>}
                      </p>
                      {knowledgeDocuments.length > 0 ? (
                        <div className="mt-2 divide-y rounded-md border">
                          {knowledgeDocuments.map((document) => (
                            <div
                              key={document.id}
                              className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
                            >
                              <div className="min-w-0">
                                <p className="truncate font-medium">
                                  {document.title}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {<span key={tUi("uploadedValue0", { value0: String(formatDate(document.created_at, uiDateLocale)) })}>{tUi("uploadedValue0", { value0: String(formatDate(document.created_at, uiDateLocale)) })}</span>}
                                </p>
                              </div>
                              <Badge variant="outline">
                                {formatValue(document.embedding_status)}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-1 text-muted-foreground">
                          <span key={tUi("noActiveDefaultDocumentsFound")}>{tUi("noActiveDefaultDocumentsFound")}</span></p>
                      )}
                    </div>
                  ) : null}
                </div>
              </section>

              <section className="space-y-3">
                <div className="flex items-center gap-2 font-medium">
                  <Bot className="h-4 w-4 text-violet-600" />
                  <span><span key={tUi("agentInformation")}>{tUi("agentInformation")}</span></span>
                </div>
                {state.agents.length === 0 ? (
                  <p className="rounded-lg border p-4 text-sm text-muted-foreground">
                    <span key={tUi("noAgentInformationWasEntered")}>{tUi("noAgentInformationWasEntered")}</span></p>
                ) : (
                  state.agents.map((agent, index) => (
                    <div key={agent.client_id} className="rounded-lg border p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="font-medium">{agent.name}</p>
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            {<span key={tUi("agentValue0Value1", { value0: String(index + 1), value1: String(formatValue(agent.type)) })}>{tUi("agentValue0Value1", { value0: String(index + 1), value1: String(formatValue(agent.type)) })}</span>}
                          </p>
                        </div>
                        {agent.agent_id ? (
                          <Badge variant="outline">
                            <CheckCircle2 className="mr-1 h-3 w-3" />
                            <span><span key={tUi("created")}>{tUi("created")}</span></span>
                          </Badge>
                        ) : null}
                      </div>

                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {agent.role_names.map((role) => (
                          <Badge key={role} variant="secondary">
                            {role}
                          </Badge>
                        ))}
                      </div>

                      <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                        <div>
                          <p className="text-xs text-muted-foreground">
                            <span key={tUi("promptChoice")}>{tUi("promptChoice")}</span></p>
                          <p key={agent.prompt_choice
                              ? formatValue(agent.prompt_choice)
                              : tUi("notApproved")} className="mt-1">
                            {agent.prompt_choice
                              ? formatValue(agent.prompt_choice)
                              : tUi("notApproved")}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">
                            <span key={tUi("createdAgentID")}>{tUi("createdAgentID")}</span></p>
                          <p className="mt-1 break-all font-mono text-xs">
                            {agent.agent_id ?? "—"}
                          </p>
                        </div>
                        {agent.type === "voice" ? (
                          <div>
                            <p className="text-xs text-muted-foreground">
                              <span key={tUi("telephonyChoice")}>{tUi("telephonyChoice")}</span></p>
                            <p key={agent.telephony_choice
                                ? formatValue(agent.telephony_choice)
                                : tUi("notSelected")} className="mt-1">
                              {agent.telephony_choice
                                ? formatValue(agent.telephony_choice)
                                : tUi("notSelected")}
                            </p>
                            {agent.telephony_identifier ? (
                              <p className="mt-1 break-all font-mono text-xs text-muted-foreground">
                                {agent.telephony_identifier}
                              </p>
                            ) : null}
                          </div>
                        ) : null}
                      </div>

                      {agent.business_summary ? (
                        <div className="mt-4">
                          <p className="text-xs text-muted-foreground">
                            <span key={tUi("businessInformation")}>{tUi("businessInformation")}</span></p>
                          <div className="mt-1 whitespace-pre-wrap rounded-md bg-muted/60 p-3 text-sm">
                            {agent.business_summary}
                          </div>
                        </div>
                      ) : null}

                      {agent.approved_prompt ? (
                        <details className="mt-4 rounded-md border">
                          <summary className="cursor-pointer px-3 py-2 text-sm font-medium">
                            <span key={tUi("approvedAgentPrompt")}>{tUi("approvedAgentPrompt")}</span></summary>
                          <pre className="max-h-72 overflow-auto whitespace-pre-wrap border-t p-3 font-sans text-xs">
                            {agent.approved_prompt}
                          </pre>
                        </details>
                      ) : null}
                    </div>
                  ))
                )}
              </section>
            </div>
          </ScrollArea>
        ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={!tenant || details.isLoading || restart.isPending}
              onClick={() => setRestartConfirmationOpen(true)}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              <span><span key={tUi("restartOnboarding")}>{tUi("restartOnboarding")}</span></span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RestartOnboardingConfirmationDialog
        mode="admin"
        tenantName={tenant?.name}
        open={restartConfirmationOpen}
        pending={restart.isPending}
        onOpenChange={setRestartConfirmationOpen}
        onConfirm={handleRestart}
      />
    </>
  )
}

function getKnowledgeSourceLabel(state: SignupOnboardingState, tUi: ReturnType<typeof useTranslations<"TenantManagement.TenantManagementTenantOnboardingDialog">>): string {
  switch (state.knowledge_source) {
    case "shopify":
      return tUi("shopifyStore")
    case "documents":
      return tUi("documents")
    case "website":
      return tUi("website")
    case "instagram":
      return "Instagram"
    case "facebook":
      return "Facebook"
    case "skipped":
      return tUi("skipped")
    case "not_selected":
      return tUi("notSelected")
    default: {
      const exhaustiveSource: never = state.knowledge_source
      return exhaustiveSource
    }
  }
}

function getKnowledgeImportStatus(
  state: SignupOnboardingState,
  documents: readonly SignupOnboardingKnowledgeDocument[],
): string {
  switch (state.knowledge_source) {
    case "website":
      return formatValue(state.website.status)
    case "documents":
      return getDocumentImportStatus(documents)
    case "instagram":
    case "facebook":
      return formatValue(state.social.status)
    // Read on demand — no import to report.
    case "shopify":
      return "Read from the store"
    case "skipped":
      return "Skipped"
    case "not_selected":
      return "Not started"
    default: {
      const exhaustiveSource: never = state.knowledge_source
      return exhaustiveSource
    }
  }
}

function getDocumentImportStatus(
  documents: readonly SignupOnboardingKnowledgeDocument[],
): string {
  if (documents.length === 0) return "No active documents"
  if (documents.some((document) => document.embedding_status === "failed")) {
    return "Needs attention"
  }
  if (
    documents.some((document) =>
      ["pending", "processing"].includes(document.embedding_status),
    )
  ) {
    return "Processing"
  }
  return "Ready"
}
